import React, { memo } from 'react';
import { Chart } from '@dexma/ui-components';
import HeatmapData, {
    CategorizedMeta,
    DatetimeMeta,
    HeatmapAxis,
    HeatmapPalette,
    HeatmapSeries,
    HeatmapValue,
} from 'domain/entity/widgets/HeatmapData';
import { ColorType } from 'domain/entity/widgetData';
import Branding from 'domain/entity/branding';
import { ChartAxisType, Resolution } from 'domain/entity/widgets/ChartData';
import i18n from 'asset/translation';
import { defaultDateFormatter } from 'adapter/in/common/utils/widgetDataUtils';
import { useProfile } from 'adapter/in/common/context/static/Profile';
import { Options, AxisTypeValue } from 'highcharts';
import { checkMemoChanges } from 'adapter/in/common/components/widgets/common/checkRedraw';

export const HEATMAP_TURBO_THRESHOLD = 100000;
export const HEATMAP_COLOR_AXIS_PADDING_RATIO = 42;
export const HEATMAP_LEGEND_WIDTH_RATIO = 1.5;
export const HEATMAP_DAYS = [
    i18n.t('common.week.MONDAY'),
    i18n.t('common.week.TUESDAY'),
    i18n.t('common.week.WEDNESDAY'),
    i18n.t('common.week.THURSDAY'),
    i18n.t('common.week.FRIDAY'),
    i18n.t('common.week.SATURDAY'),
    i18n.t('common.week.SUNDAY'),
];

type HeatmapProps = {
    data: HeatmapData;
    height: number;
    width: number;
    isOnEdit: boolean;
    decimalSeparator: string;
    thousandSeparator: string;
    branding: Branding;
    isLoading: boolean;
};

const Heatmap = (props: HeatmapProps) => {
    const { height, width, isOnEdit, data, decimalSeparator, thousandSeparator, branding, isLoading } = props;
    const { profile } = useProfile();
    const chartContract = !isLoading
        ? {
              chart: {
                  zooming: {
                      type: isOnEdit || isLoading ? undefined : 'xy',
                  },
                  height: height - 4,
                  width: width - 4,
                  type: 'heatmap',
                  borderRadius: 8,
              },

              legend: {
                  symbolWidth: width / HEATMAP_LEGEND_WIDTH_RATIO,
              },
              xAxis: {
                  categories: mapCategories(data.axisX),
                  type: mapAxisType(data.axisX) as AxisTypeValue,
                  min: mapMin(data.axisX),
                  max: mapMax(data.axisX),
              },
              yAxis: {
                  min: mapMin(data.axisY),
                  max: mapMax(data.axisY),
                  reversed: true,
                  title: { text: null },
                  categories: mapCategories(data.axisY),
                  type: mapAxisType(data.axisY) as AxisTypeValue,
                  labels: {
                      format: mapLabelFormat(data.axisY),
                  },
                  tickPositions: mapTicks(data.axisY),
                  startOnTick: false,
                  endOnTick: false,
              },
              tooltip: {
                  headerFormat: `${data.series.title}<br/>`,
                  pointFormat: mapPointFormat(data.axisX, data.axisY, data.series.units),
              },
              colorAxis: {
                  // REVIEW Not part of Heatmap options type
                  stops: mapStops(data.series.palette, branding),
                  labels: {
                      style: {
                          fontSize: '10px',
                      },
                      width: width / HEATMAP_COLOR_AXIS_PADDING_RATIO,
                  },
              },
              title: {
                  text: data.description.title,
              },
              series: [
                  {
                      type: 'heatmap',
                      turboThreshold: HEATMAP_TURBO_THRESHOLD,
                      colsize: mapColsize(data.axisX),
                      rowsize: 1,
                      data: mapData(data.series, data.axisX, data.axisY, profile.dateFormat),
                      states: {
                          hover: {
                              borderWidth: 2,
                              borderColor: branding.primaryColor,
                              brightness: 0,
                          },
                      },
                  },
              ],
          }
        : undefined;

    return (
        <Chart
            data-testid='heat-map'
            thousandsSep={thousandSeparator}
            decimalPoint={decimalSeparator}
            options={(chartContract as unknown) as Options}
        />
    );
};

const mapTicks = (axisY: HeatmapAxis): number[] | undefined => (axisY.type === ChartAxisType.DAY_HOURS ? [0, 4, 8, 12, 16, 20] : undefined);

const mapPointFormat = (axisX: HeatmapAxis, axisY: HeatmapAxis, units: string): string => {
    let xPart = '';
    let yPart = '';
    if (axisX.type === ChartAxisType.CATEGORIZED && axisY.type === ChartAxisType.CATEGORIZED) {
        return `{point.custom.tooltip}: <b>{point.value} ${units}</b>`;
    }
    if (axisX.type === ChartAxisType.DATETIME && axisY.type === ChartAxisType.WEEK_DAYS) {
        return `{point.custom.tooltip}: <b>{point.value} ${units}</b>`;
    }
    if (axisX.type === ChartAxisType.DATETIME) {
        xPart = `{point.x:%e %b, %Y}`;
    } else {
        xPart = `{point.x}`;
    }
    if (axisY.type === ChartAxisType.DAY_HOURS) {
        yPart = `{point.y}:00: <b>{point.value} ${units}</b>`;
    } else {
        yPart = `{point.y}: <b>{point.value} ${units}</b>`;
    }
    return `${xPart} ${yPart}`;
};
const mapColsize = (axis: HeatmapAxis): number => {
    if (axis.type === ChartAxisType.DATETIME) {
        switch ((axis.meta as DatetimeMeta).resolution) {
            case Resolution.W: {
                return 24 * 36e5 * 7;
            }
            case Resolution.D:
            default: {
                return 24 * 36e5;
            }
        }
    } else return 1;
};

const mapData = (series: HeatmapSeries, axisX: HeatmapAxis, axisY: HeatmapAxis, dateFormat: string) => {
    const addDays = (date: Date, numDays: number) => {
        const newDate = new Date(date);
        newDate.setDate(date.getDate() + numDays);
        return newDate;
    };
    if (axisX.type === ChartAxisType.CATEGORIZED && axisY.type === ChartAxisType.CATEGORIZED) {
        const categoriesX = (axisX.meta as CategorizedMeta).categories;
        const categoriesY = (axisY.meta as CategorizedMeta).categories;
        return series.values.length >= HEATMAP_TURBO_THRESHOLD
            ? series.values.map((value: HeatmapValue) => [Number(value.x), Number(value.y), value.v])
            : series.values.map((value: HeatmapValue) => ({
                  x: Number(value.x),
                  y: Number(value.y),
                  value: value.v,
                  custom: { tooltip: `${categoriesY[Number(value.y)]} ${categoriesX[Number(value.x)]}` },
              }));
    }
    if (axisX.type === ChartAxisType.DATETIME && axisY.type === ChartAxisType.WEEK_DAYS) {
        return series.values.length >= HEATMAP_TURBO_THRESHOLD
            ? series.values.map((value: HeatmapValue) => [Number(value.x), Number(value.y), value.v])
            : series.values.map((value: HeatmapValue) => ({
                  x: Number(value.x),
                  y: Number(value.y),
                  value: value.v,
                  custom: {
                      tooltip: `${defaultDateFormatter(addDays(value.x as Date, Number(value.y)), dateFormat.toUpperCase())} ${
                          HEATMAP_DAYS[Number(value.y)]
                      }`,
                  },
              }));
    }
    return axisX.type === ChartAxisType.DATETIME
        ? series.values.map((value: HeatmapValue) => [(value.x as Date).getTime(), Number(value.y), value.v])
        : series.values.map((value: HeatmapValue) => [Number(value.x), Number(value.y), value.v]);
};

const mapLabelFormat = (axis: HeatmapAxis): string | undefined => (axis.type === ChartAxisType.DAY_HOURS ? '{value}:00' : undefined);

const mapAxisType = (axis: HeatmapAxis): string => {
    switch (axis.type) {
        case ChartAxisType.CATEGORIZED:
            return 'category';
        case ChartAxisType.DAY_HOURS:
            return 'linear';
        case ChartAxisType.DATETIME:
        default:
            return 'datetime';
    }
};
const mapMin = (axis: HeatmapAxis): number | undefined => {
    // Highcharts has currently an issue, and if it has a 0 value on min for Heatmap
    // (with categorized axis and boost module) it explodes.
    if (axis.type === ChartAxisType.CATEGORIZED) return undefined;
    return axis.type !== ChartAxisType.DATETIME ? 0 : (axis.meta as DatetimeMeta).from.getTime();
};

const mapMax = (axis: HeatmapAxis): number | undefined => {
    switch (axis.type) {
        case ChartAxisType.CATEGORIZED:
            return (axis.meta as CategorizedMeta).categories.length - 1;
        case ChartAxisType.DAY_HOURS:
            return 23;
        case ChartAxisType.DATETIME:
        default:
            return undefined;
    }
};
const mapCategories = (axis: HeatmapAxis): string[] | undefined => {
    switch (axis.type) {
        case ChartAxisType.CATEGORIZED:
            return (axis.meta as CategorizedMeta).categories;
        case ChartAxisType.WEEK_DAYS:
            return HEATMAP_DAYS;
        default:
            return undefined;
    }
};

const mapStops = (palette: HeatmapPalette, branding: Branding) => {
    if (palette.positions.length === palette.colors.length) {
        return palette.positions.map((position: number, index: number) => {
            switch (palette.colors[index].type) {
                case ColorType.PRIMARY: {
                    return [position, branding.primaryColor];
                }
                case ColorType.BACKGROUND: {
                    return [position, branding.backgroundColor];
                }
                case ColorType.BY_DEFAULT:
                default: {
                    return [position, palette.colors[index].byDefault];
                }
            }
        });
    }
    return [palette.positions[0], palette.colors[0].byDefault];
};

export default memo(Heatmap, checkMemoChanges);
